import {ChangeDetectorRef, Component, Input, NgZone, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef} from '@angular/core';
import {Globals} from '../../../globals';
import {FileManagerService} from '../../../modules/file-manager/file-manager.service';
import {DocumentDataModel} from '../../../models/document/document-data.model';
import {MessagingService} from '../../../services/messaging.service';
import {CdkDragDrop} from '@angular/cdk/drag-drop';
import {TemplatePortal} from '@angular/cdk/portal';
import {fromEvent, Subscription} from 'rxjs';
import {filter, take} from 'rxjs/operators';
import {Overlay, OverlayRef} from '@angular/cdk/overlay';
import {ITdoFileHandle} from '../../../models/ui/i-tdo-file-handle';
import {DocumentUploadModel} from '../../../models/document/document-upload.model';
import {DocumentClassEnum} from '../../../models/enum/document/document-class.enum';
import {DocumentTypeEnum} from '../../../models/enum/document/document-type.enum';
import {FileHelperService} from '../../../services/file-helper.service';
import {DocumentFileTypeEnum} from '../../../models/enum/document/document-file-type.enum';
import {TdoClipboardBaseModel} from '../../../models/clipboard/tdo-clipboard-base.model';
import {MessagingConstants} from '../../../models/MessagingConstants';
import { TdoImagingService } from '../../imaging/tdo-imaging.service';
import { CaseTooth } from '../../imaging/models/CaseTooth';
import {SignalrService} from '../../../services/socket/signalr.service';
import {UiService} from '../../../services/ui.service';
import {DocumentDataImageModel} from '../tdo-organizer/models/document-data-image.model';
import {OrganizerOpenInImagingModel} from '../tdo-organizer/models/organizer-open-in-imaging.model';
import { DOCUMENT_TAG } from 'src/app/models/constants/document-tag.model';

@Component({
  standalone: false,
  selector: 'app-tdo-pre-post-op-control',
  templateUrl: './tdo-pre-post-op-control.component.html',
  styleUrls: ['./tdo-pre-post-op-control.component.scss']
})
export class TdoPrePostOpControlComponent implements OnInit, OnDestroy {
  @ViewChild('pasteMenu') pasteMenu: TemplateRef<any>;
  @Input() isExpanded = true;
  caseWorkItemId: string;
  isMouseOver = false;
  itemName: string;
  isUploading = false;
  isDesktopAcquire = false;
  lastItemName: string = '';

  preopDocs: DocumentDataModel[] = [];
  postopDocs: DocumentDataModel[] = [];
  preopIndex = 0;
  postopIndex = 0;
  documents: any;
  preopImageSrc: string;
  postopImageSrc: string;
  isContextOpen = false;
  toothDetail: CaseTooth[];
  private overlayRef: OverlayRef | null;
  private sub: Subscription;

  /** Bust browser cache on <img src> when the API path is unchanged after save. */
  private prePostImageCacheBust = Date.now();
  private visRefreshDebounce: ReturnType<typeof setTimeout> | null = null;

  /** Same-origin Studio window writes localStorage; `storage` fires here only (other windows). */
  private readonly onLandingPrePostStorage = (e: StorageEvent): void => {
    if (e.key !== MessagingConstants.LOCAL_STORAGE_PREPOST_LANDING_REFRESH || !e.newValue) {
      return;
    }
    try {
      const data = JSON.parse(e.newValue) as { patientId?: string };
      if (data.patientId && data.patientId.toLowerCase() === (this.globals.currentPatientId ?? '').toLowerCase()) {
        this.ngZone.run(() => this.checkForDocumentImages());
      }
    } catch {
      /* ignore */
    }
  };

  /** When returning to this tab/window, refetch (covers missed SignalR while backgrounded). */
  private readonly onLandingVisibility = (): void => {
    if (typeof document === 'undefined' || document.visibilityState !== 'visible') {
      return;
    }
    if (this.visRefreshDebounce) {
      clearTimeout(this.visRefreshDebounce);
    }
    this.visRefreshDebounce = setTimeout(() => {
      this.visRefreshDebounce = null;
      this.ngZone.run(() => this.checkForDocumentImages());
    }, 250);
  };


  constructor(public globals: Globals,
              private readonly uiService: UiService,
              private readonly overlay: Overlay,
              private readonly viewContainerRef: ViewContainerRef,
              private readonly messagingService: MessagingService,
              private readonly fileHelperService: FileHelperService,
              private readonly drawService: TdoImagingService,
              private readonly socketService: SignalrService,
              private readonly fileManagerService: FileManagerService,
              private readonly cdr: ChangeDetectorRef,
              private readonly ngZone: NgZone) {
    this.caseWorkItemId = this.globals.currentPatientCaseWorkItemID;
  }

  ngOnInit(): void {
    //this.checkForDocumentImages();

    this.socketService.messageUser.subscribe((sockRes) => {
      switch (sockRes.messageType) {
        case 'UPDATE_DOCUMENT':
          if (!this.shouldRefreshPrePostForSocketPayload(sockRes.message)) {
            return;
          }
          // SignalR runs outside Angular's zone; run refresh in-zone so HTTP + CD pick up Studio saves immediately.
          this.ngZone.run(() => this.checkForDocumentImages());
          break;
        case 'brokerImageData':
          // Desktop acquire: service broker broadcasts { type: 'document-saved', patientId } via WebSocket
          // after the backend upload is confirmed. React only to that subtype (not the early Phase 1 image burst).
          try {
            const obj = JSON.parse(sockRes.message as unknown as string);
            if (obj?.type === 'document-saved') {
              const pid = obj.patientId;
              if (!pid || String(pid).toLowerCase() === (this.globals.currentPatientId ?? '').toLowerCase()) {
                this.ngZone.run(() => this.checkForDocumentImages());
              }
            }
          } catch { /* ignore malformed messages */ }
          break;
      }
    });
    this.messagingService.componentMessageCall.subscribe((response) => {
      switch (response.event) {
        case MessagingConstants.CASE_CHANGED_EVENT:
        case MessagingConstants.IO_TAGS_UPDATED:
          this.ngZone.run(() => this.checkForDocumentImages());
          break;
        case MessagingConstants.OPEN_PREOP_IMAGES:
          this.openInStudio('preop');
          break;
      }
    });
    let currentPatientCaseWorkItemID: string;
    if (this.isDesktopAcquire) {
      // get tooth number from currentPatientCaseWorkItemID or currentPatientCase

      if (this.globals.currentPatientCaseWorkItemID === '') {
        currentPatientCaseWorkItemID = '00000000-0000-0000-0000-000000000000';
      }
      this.drawService.getCaseTooth(this.globals.currentPatientCaseId, currentPatientCaseWorkItemID, this.globals.currentToothNumberingId).subscribe((caseToothModelResponse) => {

        if (caseToothModelResponse) {
          this.toothDetail = caseToothModelResponse;
        }
      });

      this.drawService.initServiceBroker();
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.onLandingPrePostStorage);
      document.addEventListener('visibilitychange', this.onLandingVisibility);
    }
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', this.onLandingPrePostStorage);
      document.removeEventListener('visibilitychange', this.onLandingVisibility);
    }
    if (this.visRefreshDebounce) {
      clearTimeout(this.visRefreshDebounce);
    }
  }

  /** Matches organizer behavior: only refresh when the SignalR payload targets the active patient (if patientId is present). */
  private shouldRefreshPrePostForSocketPayload(payload: unknown): boolean {
    if (payload == null || payload === '') {
      return true;
    }
    let msg: Record<string, unknown>;
    if (typeof payload === 'string') {
      try {
        msg = JSON.parse(payload) as Record<string, unknown>;
      } catch {
        return true;
      }
    } else if (typeof payload === 'object') {
      msg = payload as Record<string, unknown>;
    } else {
      return true;
    }
    const pid = msg['patientId'];
    if (pid === undefined || pid === null || pid === '') {
      return true;
    }
    return String(pid).toLowerCase() === String(this.globals.currentPatientId ?? '').toLowerCase();
  }

  checkForDocumentImages(type: string = '') {
    if (type === '') {
      this.preopDocs = [];
      this.preopIndex = 0;
      this.preopImageSrc = '';
      this.postopDocs = [];
      this.postopIndex = 0;
      this.postopImageSrc = '';
    }

    if (!this.globals.currentPatientCaseId || !this.globals.currentPatientId) {
      return;
    }

    if (type === 'preop') {
      this.preopDocs = [];
      this.preopIndex = 0;
      this.preopImageSrc = '';
    }

    if (type === 'postop') {
      this.postopDocs = [];
      this.postopIndex = 0;
      this.postopImageSrc = '';
    }

    if (this.globals.currentPatientId != null && this.globals.currentPatientCaseId != null && this.globals.currentPatientCaseId != 'null' && this.globals.currentPatientId != 'null' ){

      this.fileManagerService.getPrePostOpForLanding(this.globals.currentPatientId, this.globals.currentPatientCaseId).subscribe((res) => {

        this.documents = res;
        this.preopDocs = this.documents.filter(p => p.dataDocumentTags.toLowerCase() == DOCUMENT_TAG.PreOp.toLowerCase());
        this.postopDocs = this.documents.filter(p => p.dataDocumentTags.toLowerCase() == DOCUMENT_TAG.PostOp.toLowerCase());  

        if (this.preopDocs) {
          this.preopImageSrc = this.fileManagerService.getDocumentUrlFromDocument(this.preopDocs[this.preopIndex]);
        }
        if (this.postopDocs) {
          this.postopImageSrc = this.fileManagerService.getDocumentUrlFromDocument(this.postopDocs[this.postopIndex]);
        }
        this.prePostImageCacheBust = Date.now();
        this.cdr.detectChanges();
      });
    }
  }

  // onMouseOver(event: MouseEvent) {
  //   this.isMouseOver = true;
  // }

  // onMouseOut(event: MouseEvent) {
  //   console.log("PrePost Op Moseout");
  //   this.isMouseOver = false;
  //   this.itemName = '';
  // }

  onItemMouseOver(itemName: string) {
    if (itemName === 'preop') {
      if (!this.isContextOpen) {
        this.lastItemName = 'Pre Op';
      }
    } else {
      if (!this.isContextOpen) {
        this.lastItemName = 'Post Op';
      }
    }
    this.isMouseOver = true;
    this.itemName = itemName;
  }

  onItemMouseOut(itemName: string) {
    this.isMouseOver = false;
    if (!this.isContextOpen) {
      this.itemName = '';
    }
  }

  isSelectedItem(itemName: string) {
    if (itemName) {
      return this.itemName === itemName;
    } else {
      return '';
    }
  }

  onRightMouseClick(event: MouseEvent, title: string, type: string) {
    this.itemName = type;
    this.openContext(event, this.pasteMenu, null);
  }

  onPreOpDropped(event: CdkDragDrop<any>) {
    let documents = event.item.data as DocumentDataImageModel[];
    documents = documents.filter(p => p.model.dataDocument.enumDocumentFileTypeID.toUpperCase() === '0000001B-1000-0000-0000-000000000003');
    const jpgDocs = documents.map(p => p.model.dataDocument.DocumentID);
    this.fileManagerService.addMultiDocumentsTags(jpgDocs, [DOCUMENT_TAG.PreOp], this.globals.currentPatientCaseId, this.globals.currentPatientId).subscribe((res) => {
      /*for (const mod of res) {
        this.preopDocs.unshift(mod);
      }*/
      setTimeout(() => {
        this.checkForDocumentImages();
      }, 1000);
    });
  }

  onPostOpDropped(event: CdkDragDrop<any>) {
    let documents = event.item.data as DocumentDataImageModel[];
    documents = documents.filter(p => p.model.dataDocument.enumDocumentFileTypeID.toUpperCase() === '0000001B-1000-0000-0000-000000000003');
    const jpgDocs = documents.map(p => p.model.dataDocument.DocumentID);
    this.fileManagerService.addMultiDocumentsTags(jpgDocs, [DOCUMENT_TAG.PostOp], this.globals.currentPatientCaseId, this.globals.currentPatientId).subscribe((res) => {
      /*for (const mod of res) {
        this.postopDocs.unshift(mod);
      }*/
      setTimeout(() => {
        this.checkForDocumentImages();
      }, 1000);
    });
  }

  // Context menu
  // Context boilerplate
  openContext({x, y}: MouseEvent, template: TemplateRef<any>, data: any, closeOnItemClick = true): void {
    this.isContextOpen = true;
    this.closeContext();
    x = x + 50;
    y = y - 20;
    const positionStrategy = this.overlay.position().flexibleConnectedTo({x, y}).withPositions([
      {
        originX: 'end',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'top'
      }
    ]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close()
    });

    this.overlayRef.attach(new TemplatePortal(template, this.viewContainerRef, {$implicit: data}));

    this.sub = fromEvent<MouseEvent>(document, 'click')
      .pipe(
        filter(event => {
          if (closeOnItemClick) {
            this.closeContext();
          } else {
            const clickTarget = event.target as HTMLElement;
            return !!this.overlayRef && !this.overlayRef.overlayElement.contains(clickTarget);
          }
        }), take(1)).subscribe(() => this.closeContext());
  }

  closeContext(): void {
    this.sub !== undefined && this.sub.unsubscribe();
    if (this.overlayRef) {
      this.isContextOpen = false;
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  onFilesDropped(event: ITdoFileHandle[], type: string) {
    event = event.filter(p => p.file.type === 'image/jpeg' || p.file.type === 'image/png');
    this.itemName = type;
    const file: File = event[0].file;
    this.uploadDocuments([file]);
  }

  // Currently only handles one file at a time.
  uploadDocuments(files: File[]) {
    let tag = '';
    switch (this.itemName) {
      case 'preop':
        tag = DOCUMENT_TAG.PreOp;
        break;
      case 'postop':
        tag = DOCUMENT_TAG.PostOp;
        break;
    }

    this.fileHelperService.getDataAndThumbnailFromFile(files[0]).subscribe((mod) => {
      const fileModel = mod;
      const uploadModel = new DocumentUploadModel();
      uploadModel.blob = fileModel.fileDataBlob;
      uploadModel.blobThumbnail = fileModel.thumbnailDataBlob;
      uploadModel.docClassId = DocumentClassEnum.Original;
      uploadModel.docTypeId = DocumentTypeEnum.MedicalImaging;
      uploadModel.hasAnnotation = false;
      uploadModel.docTagIds = [tag];

      switch (fileModel.fileMimeType) {
        case FileHelperService.MIME_JPG:
        case FileHelperService.MIME_PNG:
          uploadModel.docFileTypeId = DocumentFileTypeEnum.JPG;
          break;
      }

      uploadModel.patientId = this.globals.currentPatientId;
      uploadModel.patientCaseId = this.globals.currentPatientCaseId;

      this.fileManagerService.uploadDocuments([uploadModel]).subscribe((response) => {
        const res = response.documents[0];
        // this.imageUrl = this.fileManagerService.getDocumentUrlFromDocument(res);
        this.fileManagerService.addMultiDocumentsTags([res.DocumentID], [tag]).subscribe((responseAddTag) => {
          this.isUploading = false;
          this.checkForDocumentImages();
        });
      });
    });
  }

  getImageSource(type: string): string {
    let path: string | undefined;
    switch (type) {
      case 'preop':
        path = this.fileManagerService.getDocumentUrlFromDocument(this.preopDocs[this.preopIndex]);
        break;
      case 'postop':
        path = this.fileManagerService.getDocumentUrlFromDocument(this.postopDocs[this.postopIndex]);
        break;
      default:
        path = undefined;
    }
    if (!path) {
      return '';
    }
    const sep = path.includes('?') ? '&' : '?';
    return `${path}${sep}_cb=${this.prePostImageCacheBust}`;
  }

  onAcquire(data: any) {
    const tags: string[] = [];
    let isOpenImage = false;
    switch (this.itemName) {
      case 'preop':
        tags.push('0000001D-1000-0000-0000-000000000008');
        break;
      case 'postop':
        tags.push('0000001D-1000-0000-0000-000000000014');
        break;
    }
    console.log('tags ', tags);
    localStorage.setItem('tags-imaging', JSON.stringify(tags));

    if (data === 'open-image') {
      isOpenImage = true;
    }
    this.uiService.openAcquireDefaultDevice(tags, false, isOpenImage);    
  }


  onPaste(data: any) {
    const clipboardData = localStorage.getItem('tdo-clipboard');
    const clipboardModel: TdoClipboardBaseModel = JSON.parse(clipboardData);

    if (clipboardModel.type !== TdoClipboardBaseModel.TYPE_DOCUMENT_DATA) {
      return;
    }

    let tag: string;
    switch (this.itemName) {
      case 'preop':
        tag = '0000001D-1000-0000-0000-000000000008';
        break;
      case 'postop':
        tag = '0000001D-1000-0000-0000-000000000014';
        break;
    }

    const ids = clipboardModel.copyData as string[];
    this.isUploading = true;
    this.fileManagerService.addMultiDocumentsTags(ids, [tag], this.globals.currentPatientCaseId, this.globals.currentPatientId).subscribe((response) => {
      this.isUploading = false;
      this.checkForDocumentImages();
    });
  }

  onDoubleClick(type: string) {
    let items: DocumentDataModel[];
    let item: DocumentDataModel;
    switch (type) {
      case 'preop':
        items = this.preopDocs;
        item = items[this.preopIndex];
        break;
      case 'postop':
        items = this.postopDocs;
        item = items[this.postopIndex];
        break;
    }

    this.itemName = type;

    if (item) {
      const openInImagingModel = new OrganizerOpenInImagingModel();
      openInImagingModel.documents = [item];
      localStorage.setItem('open-in-imaging', JSON.stringify(openInImagingModel));
      localStorage.setItem('is-Start-Folder-Monitor', JSON.stringify('false'));
      this.onAcquire('open-image');
    } else {
      this.onAcquire(null);
    }
  }

  openInStudio(type: string) {
    let items: DocumentDataModel[];
    switch (type) {
      case 'preop':
        items = this.preopDocs;
        break;
      case 'postop':
        items = this.postopDocs;
        break;
    }

    const openInImagingModel = new OrganizerOpenInImagingModel();
    openInImagingModel.documents = items;
    localStorage.setItem('open-in-imaging', JSON.stringify(openInImagingModel));
    localStorage.setItem('open-blank-image', JSON.stringify('true'));
    localStorage.setItem('is-Start-Folder-Monitor', JSON.stringify('false'));
    this.itemName = type;
    this.onAcquire('open-image');
  }


  isPasteableData() {
    return !!localStorage.getItem('tdo-clipboard');
  }

  openAll(data: any) {
    const openInImagingModel = new OrganizerOpenInImagingModel();
    switch (this.lastItemName) {
      case 'Pre Op':
        openInImagingModel.documents = this.preopDocs;
        break;
      case 'Post Op':
        openInImagingModel.documents = this.postopDocs;
        break;
    }

    localStorage.setItem('open-in-imaging', JSON.stringify(openInImagingModel));
    this.uiService.openImagingWindow();
  }

  onRemoveAssociation(data: any) {
    let document: DocumentDataModel;
    switch (this.lastItemName) {
      case 'Pre Op':
        document = this.preopDocs[this.preopIndex];
        this.fileManagerService.removeDocumentTags(document.DocumentID, null, [DOCUMENT_TAG.PreOp]).subscribe((res) => {
          this.checkForDocumentImages();
        });
        break;
      case 'Post Op':
        document = this.postopDocs[this.postopIndex];
        this.fileManagerService.removeDocumentTags(document.DocumentID, null, [DOCUMENT_TAG.PostOp]).subscribe((res) => {
          this.checkForDocumentImages();
        });
        break;
    }
  }


}
